import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DEMO } from '../demo/index.js';
import {
  debeRecargar,
  huellaDeUrls,
  urlsDeAssetsDelDocumento,
  urlsDeAssetsDelHtml,
} from '../lib/versionDeLaApp.js';

/**
 * Mantiene la pestaña en la versión que está servida.
 *
 * El problema, en concreto: la aplicación trae todo el JavaScript en la carga inicial, así
 * que reemplazar el contenedor durante un despliegue **no rompe** una pestaña abierta —el
 * que está trabajando sigue trabajando— pero esa pestaña se queda con el código que cargó
 * hasta que alguien la recargue. Casi siempre se cura solo: durante el despliegue el motor
 * deja de contestar, aparece la pantalla de "estamos actualizando" y al volver recarga.
 * Pero si en esa ventana el usuario no le pidió NADA al servidor —estaba leyendo, o con la
 * pestaña de fondo—, la pantalla nunca aparece y la pestaña queda vieja en silencio.
 *
 * ── CUÁNDO SE RECARGA, que es lo delicado ──
 *
 * Nunca en medio de algo. Una recarga a destiempo le borra al usuario lo que estaba
 * escribiendo, y eso es peor que dejarlo con código viejo. Solo en dos momentos, los dos
 * elegidos porque ahí no hay nada en vuelo:
 *
 *   - **al volver a la pestaña** después de tenerla de fondo (no estaba escribiendo);
 *   - **al navegar** a otra pantalla (ya aceptó perder lo que tenía a la vista).
 *
 * Si detecta una versión nueva mientras el usuario trabaja sin moverse, **espera**. Que
 * espere de más solo posterga; interrumpirlo le cuesta trabajo de verdad.
 */

/** Cada cuánto se pregunta. Barato: es el index.html, unos cientos de bytes. */
const INTERVALO_MS = 10 * 60 * 1000;
/** Hacia qué versión ya se recargó esta pestaña — para no entrar en bucle. */
const CLAVE_YA_RECARGADA = 'supernova:version-recargada-hacia';

function leerYaRecargada(): string | null {
  try {
    return sessionStorage.getItem(CLAVE_YA_RECARGADA);
  } catch {
    return null; // pestaña privada, almacenamiento bloqueado: se sigue sin memoria
  }
}

export function VigilanteDeVersion() {
  const location = useLocation();
  /** La versión con la que arrancó ESTA pestaña. Se lee una vez y no cambia. */
  const cargada = useRef<string>('');
  /** La versión nueva detectada y todavía sin aplicar. */
  const pendiente = useRef<string | null>(null);

  if (cargada.current === '' && typeof document !== 'undefined') {
    cargada.current = huellaDeUrls(urlsDeAssetsDelDocumento(document));
  }

  const aplicar = useCallback(() => {
    const servida = pendiente.current;
    if (!servida) return;
    try {
      sessionStorage.setItem(CLAVE_YA_RECARGADA, servida);
    } catch {
      // Sin almacenamiento no hay red contra el bucle, pero recargar una vez es correcto.
    }
    window.location.reload();
  }, []);

  const preguntar = useCallback(async () => {
    if (pendiente.current) return; // ya hay una detectada esperando su momento
    try {
      // `no-store` para que no conteste el propio navegador con la copia vieja: eso
      // haria que la pestaña nunca se enterara de nada.
      const res = await fetch('/index.html', { cache: 'no-store' });
      if (!res.ok) return;
      const servida = huellaDeUrls(urlsDeAssetsDelHtml(await res.text()));
      if (debeRecargar({ cargada: cargada.current, servida, yaRecargadaHacia: leerYaRecargada() })) {
        pendiente.current = servida;
        // Si la pestaña está de fondo justo ahora, es el mejor momento posible.
        if (document.visibilityState === 'hidden') aplicar();
      }
    } catch {
      // Sin red, o el motor no está: no es asunto de esta vigilancia.
    }
  }, [aplicar]);

  useEffect(() => {
    if (DEMO) return; // el demo es un archivo suelto, no hay servidor que preguntar
    const id = window.setInterval(() => void preguntar(), INTERVALO_MS);
    // Al volver a la pestaña: es el momento MÁS probable de que haya habido un
    // despliegue mientras el usuario no estaba, y el más seguro para recargar.
    const alVolver = () => {
      if (document.visibilityState !== 'visible') return;
      if (pendiente.current) aplicar();
      else void preguntar();
    };
    document.addEventListener('visibilitychange', alVolver);
    void preguntar();
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [preguntar, aplicar]);

  // Al navegar: el usuario ya dejó atrás lo que tenía a la vista, así que una recarga
  // acá no le cuesta nada.
  useEffect(() => {
    if (pendiente.current) aplicar();
  }, [location.pathname, aplicar]);

  return null;
}
